import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { ClipboardCheck, FileText, ArrowRight, Sparkles, Headphones, BookOpen, PenLine, Mic, Layers } from "lucide-react";

const COUNT_ICONS = { reading: BookOpen, listening: Headphones, writing: PenLine, speaking: Mic };

const LEVEL_CARDS = [
  { skill: "reading", label: "Reading", fr: "Compréhension écrite", icon: BookOpen, desc: "Drill A1 → C2 reading questions from every mock, one level at a time." },
  { skill: "listening", label: "Listening", fr: "Compréhension orale", icon: Headphones, desc: "Drill A1 → C2 listening questions from every mock, one level at a time." },
];

function CountRow({ counts }) {
  return (
    <div className="mt-4 flex flex-wrap gap-3 text-xs text-muted-foreground">
      {Object.entries(counts).map(([k, v]) => {
        const Icon = COUNT_ICONS[k];
        return <span key={k} className="inline-flex items-center gap-1"><Icon className="h-3.5 w-3.5" /> {v}</span>;
      })}
    </div>
  );
}

export default function Tests() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/tcf/assessments").then((r) => setData(r.data)).catch((e) => console.error("assessments", e)); }, []);

  return (
    <div data-testid="tests-page">
      <h1 className="font-display text-4xl font-semibold">Mock Tests</h1>
      <p className="text-muted-foreground mt-2">Full TCF Canada mock tests with the same structure as the real exam.</p>

      {/* Practice by level */}
      <h2 className="mt-8 font-display text-2xl font-semibold flex items-center gap-2"><Layers className="h-5 w-5 text-primary" /> Practice by level</h2>
      <p className="text-sm text-muted-foreground">Focus on one CEFR level at a time and track your score for each question type.</p>
      <div className="grid sm:grid-cols-2 gap-5 mt-4" data-testid="tests-level-practice">
        {LEVEL_CARDS.map((c, i) => {
          const Icon = c.icon;
          return (
            <motion.div key={c.skill} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
              <Link to={`/practice/levels?skill=${c.skill}`} data-testid={`level-practice-${c.skill}`}
                className="group block rounded-2xl bg-card border border-border p-6 hover:shadow-lg hover:-translate-y-1 transition-all h-full">
                <div className="h-11 w-11 rounded-xl emerald-surface grid place-items-center"><Icon className="h-5 w-5 text-white" /></div>
                <h3 className="mt-4 font-semibold text-lg">{c.label} · practice by level</h3>
                <p className="text-sm text-muted-foreground mt-1">{c.desc}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {["A1", "A2", "B1", "B2", "C1", "C2"].map((l) => (
                    <span key={l} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground">{l}</span>
                  ))}
                </div>
                <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary">Start practising <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></div>
              </Link>
            </motion.div>
          );
        })}
      </div>

      {data && (
        <>
          <h2 className="mt-8 font-display text-2xl font-semibold flex items-center gap-2"><FileText className="h-5 w-5 text-primary" /> Full mock tests</h2>
          <p className="text-sm text-muted-foreground">Same structure as the real exam.</p>
          <div className="grid sm:grid-cols-2 gap-5 mt-4">
            {data.mocks.map((m, i) => (
              <motion.div key={m.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
                <Link to={`/tests/${m.id}`} data-testid={`start-${m.id}`}
                  className="group block rounded-2xl bg-card border border-border p-6 hover:shadow-lg hover:-translate-y-1 transition-all h-full">
                  <div className="h-11 w-11 rounded-xl emerald-surface grid place-items-center"><Sparkles className="h-5 w-5 text-white" /></div>
                  <h3 className="mt-4 font-semibold text-lg">{m.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{m.description}</p>
                  <CountRow counts={m.counts} />
                  <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary">Start test <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></div>
                </Link>
              </motion.div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
